import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Loader2, MapPin, Hash } from "lucide-react";
import { Hotel, HotelFormData } from "@/types/hotel";
import { HotelImageUpload } from "./HotelImageUpload";
import { useCountries } from "@/hooks/useCountries";
import { useCities } from "@/hooks/useCities";

interface HotelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingHotel: Hotel | null;
  onSubmit: (formData: HotelFormData, editingHotel: Hotel | null, imageFile: File | null) => Promise<boolean>;
}

export const HotelDialog = ({ open, onOpenChange, editingHotel, onSubmit }: HotelDialogProps) => {
  const [formData, setFormData] = useState<HotelFormData>({
    country: '',
    city: '',
    name_ar: '',
    name_en: '',
    stars: 1,
    is_default: false,
    image_url: '',
    hotel_number: '',
    google_maps_url: '',
    address: ''
  });
  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Fetch countries and cities
  const { data: countries = [] } = useCountries();
  const { data: cities = [] } = useCities(selectedCountryId || null);

  useEffect(() => {
    if (editingHotel) {
      setFormData({
        country: editingHotel.country,
        city: editingHotel.city,
        name_ar: editingHotel.name_ar,
        name_en: editingHotel.name_en,
        stars: editingHotel.stars,
        is_default: editingHotel.is_default,
        image_url: editingHotel.image_url || '',
        hotel_number: editingHotel.hotel_number || '',
        google_maps_url: editingHotel.google_maps_url || '',
        address: editingHotel.address || ''
      });
      // Find and set country ID when editing
      const country = countries.find(c => c.name_ar === editingHotel.country);
      if (country) {
        setSelectedCountryId(country.id);
      }
    } else {
      resetForm();
    }
    setImageFile(null);
    setImagePreview(null);
  }, [editingHotel, open, countries]);

  const resetForm = () => {
    setFormData({
      country: '',
      city: '',
      name_ar: '',
      name_en: '',
      stars: 1,
      is_default: false,
      image_url: '',
      hotel_number: '',
      google_maps_url: '',
      address: ''
    });
    setSelectedCountryId('');
    setImageFile(null);
    setImagePreview(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setFormData(prev => ({ ...prev, image_url: '' }));
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, image_url: e.target.value }));
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const success = await onSubmit(formData, editingHotel, imageFile);
    setIsSubmitting(false);
    if (success) {
      onOpenChange(false);
      resetForm();
    }
  };

  const renderStarSelector = () => {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, stars: index + 1 }))}
            className="p-1 rounded transition-colors hover:bg-gray-100"
          >
            <Star
              className={`w-6 h-6 ${
                index < formData.stars 
                  ? 'text-yellow-400 fill-current' 
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        <span className="mr-2 text-sm text-gray-600">
          ({formData.stars} نجمة)
        </span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>
            {editingHotel ? 'تعديل الفندق' : 'إضافة فندق جديد'}
          </DialogTitle>
          <DialogDescription>
            {editingHotel ? 'قم بتعديل بيانات الفندق' : 'قم بإدخال بيانات الفندق الجديد'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">البيانات الأساسية</TabsTrigger>
              <TabsTrigger value="details">التفاصيل الإضافية</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name_ar">الاسم بالعربية</Label>
                <Input
                  id="name_ar"
                  value={formData.name_ar}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                  placeholder="مثال: فندق برج العرب"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
                <Input
                  id="name_en"
                  value={formData.name_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                  placeholder="مثال: Burj Al Arab Hotel"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">الدولة</Label>
                  <Select
                    value={selectedCountryId}
                    onValueChange={(value) => {
                      setSelectedCountryId(value);
                      const country = countries.find(c => c.id === value);
                      if (country) {
                        setFormData(prev => ({ ...prev, country: country.name_ar, city: '' }));
                      }
                    }}
                    required
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder="اختر الدولة" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country.id} value={country.id}>
                          <span className="flex items-center gap-2">
                            {country.flag_emoji && <span>{country.flag_emoji}</span>}
                            {country.name_ar}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">المدينة</Label>
                  <Select
                    value={formData.city}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, city: value }))}
                    disabled={!selectedCountryId}
                    required
                  >
                    <SelectTrigger id="city">
                      <SelectValue placeholder={selectedCountryId ? "اختر المدينة" : "اختر الدولة أولاً"} />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map(city => (
                        <SelectItem key={city.id} value={city.name_ar}>
                          {city.name_ar}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="details" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hotel_number" className="flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  رقم الفندق
                </Label>
                <Input
                  id="hotel_number"
                  value={formData.hotel_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, hotel_number: e.target.value }))}
                  placeholder="مثال: HTL-001"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">العنوان</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="أدخل العنوان الكامل للفندق"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="google_maps_url" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  رابط خرائط جوجل
                </Label>
                <Input
                  id="google_maps_url"
                  value={formData.google_maps_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, google_maps_url: e.target.value }))}
                  placeholder="https://maps.google.com/..."
                  type="url"
                />
              </div>

              <HotelImageUpload
                imagePreview={imagePreview}
                imageUrl={formData.image_url}
                onFileChange={handleFileChange}
                onUrlChange={handleUrlChange}
                isUploading={isSubmitting}
                imageUrlValue={formData.image_url || ''}
              />
              
              <div className="space-y-2">
                <Label>عدد النجوم</Label>
                {renderStarSelector()}
              </div>

              <div className="flex items-center space-x-2 space-x-reverse">
                <Checkbox
                  id="is_default"
                  checked={formData.is_default}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, is_default: checked as boolean }))
                  }
                />
                <Label htmlFor="is_default" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  الفندق الافتراضي (سيظهر أولاً في قوائم العروض السياحية)
                </Label>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              إلغاء
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
              {editingHotel ? 'تحديث' : 'إضافة'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};